import React from 'react';
import { Car, Shield, FileText, Globe, Truck, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import Newsletter from '../components/Newsletter';

const AutomotiveProcurement = () => {
  const services = [
    {
      icon: Car,
      title: "Exotic Vehicle Sourcing",
      description: "Access to rare, limited-production, and high-performance vehicles from North American and European markets"
    },
    {
      icon: Globe,
      title: "International Import/Export",
      description: "Licensed brokerage services with full compliance for cross-border vehicle transactions"
    },
    {
      icon: FileText,
      title: "Title Processing & Documentation",
      description: "Complete handling of ownership transfers, export documents, and customs clearance paperwork"
    },
    {
      icon: Shield,
      title: "Authenticity Verification",
      description: "Full vehicle history reports, inspection services, and provenance documentation"
    },
    {
      icon: Truck,
      title: "Secure Transportation",
      description: "Enclosed shipping, insurance coverage, and door-to-door delivery anywhere in the Middle East"
    },
    {
      icon: Award,
      title: "Collector & Fleet Services",
      description: "Specialized procurement for collectors, government fleets, and agency-specific requirements"
    }
  ];

  const vehicleTypes = [
    {
      title: "Exotic Sports Cars",
      description: "Ferrari, Lamborghini, McLaren, Porsche, and other high-performance marques",
      examples: ["Limited production runs", "Special editions", "Track-ready models", "Heritage vehicles"]
    },
    {
      title: "Luxury Sedans & SUVs",
      description: "Premium vehicles from Mercedes-Benz, BMW, Bentley, Rolls-Royce, and Range Rover",
      examples: ["Executive sedans", "Armored variants", "Long-wheelbase models", "Custom specifications"]
    },
    {
      title: "American Muscle & Classics",
      description: "Chevrolet Corvette, Ford Mustang, Dodge Challenger, and classic American vehicles",
      examples: ["Modern performance variants", "Restored classics", "Limited editions", "Special paint options"]
    },
    {
      title: "Collector Vehicles",
      description: "Rare, vintage, and investment-grade automobiles from all eras",
      examples: ["Classic European sports cars", "American muscle heritage", "Investment pieces", "Concours-ready vehicles"]
    }
  ];

  const process = [
    {
      step: "1",
      title: "Consultation",
      description: "Discuss your requirements, budget, timeline, and specific vehicle preferences"
    },
    {
      step: "2",
      title: "Sourcing",
      description: "Locate and verify vehicles through our North American and European dealer networks"
    },
    {
      step: "3",
      title: "Inspection",
      description: "Comprehensive pre-purchase inspection, history verification, and documentation review"
    },
    {
      step: "4",
      title: "Purchase & Export",
      description: "Handle purchase transaction, title processing, and export documentation preparation"
    },
    {
      step: "5",
      title: "Shipping",
      description: "Secure enclosed transport with full insurance coverage to Middle East destination"
    },
    {
      step: "6",
      title: "Delivery",
      description: "Customs clearance, final inspection, and white-glove delivery to your location"
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with Exotic Vehicles */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_consulting-synergy/artifacts/u0l9hqq5_9D9B2815-C712-4A48-B98D-55C8653116CE.png"
            alt="Exotic Luxury Vehicles"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/65"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center py-32">
          <h1 className="luxury-heading text-5xl md:text-7xl text-white mb-8 leading-tight">
            Exotic Vehicle Procurement
            <span className="text-[#C9A55A]"> & International Automotive Brokerage</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-12 max-w-4xl mx-auto leading-relaxed">
            Access to North American, European, and specialty vehicles through licensed import/export channels.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button 
              asChild
              className="luxury-button bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-lg px-12 py-7 smooth-transition shadow-xl"
            >
              <Link to="/contact">
                Request Vehicle Sourcing
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-[#C9A55A] text-[#C9A55A] hover:bg-[#C9A55A] hover:text-black font-semibold text-lg px-12 py-7 smooth-transition"
            >
              <Link to="/services">
                View All Services
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Spacer */}
      <div className="h-20 bg-black"></div>

      {/* Services Grid */}
      <section className="py-32 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="luxury-heading text-5xl md:text-6xl text-white mb-6">Our Automotive Services</h2>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
              End-to-end vehicle procurement with complete import/export compliance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="luxury-card bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 hover:border-[#C9A55A]/30 group cursor-pointer">
                <CardContent className="p-10">
                  <div className="w-16 h-16 bg-[#C9A55A]/10 rounded-lg flex items-center justify-center mb-8 group-hover:bg-[#C9A55A]/20 smooth-transition border border-[#C9A55A]/20">
                    <service.icon className="text-[#C9A55A]" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">{service.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Types */}
      <section className="py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="luxury-heading text-5xl md:text-6xl text-white mb-6">Vehicle Categories</h2>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
              Access to the world's most sought-after vehicles
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {vehicleTypes.map((type, index) => (
              <Card key={index} className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
                <CardContent className="p-10">
                  <h3 className="text-2xl font-bold text-white mb-3">{type.title}</h3>
                  <p className="text-gray-400 mb-6">{type.description}</p>
                  <div className="space-y-2">
                    {type.examples.map((example, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-[#C9A55A] rounded-full"></div>
                        <span className="text-gray-300 text-sm">{example}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-32 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="luxury-heading text-5xl md:text-6xl text-white mb-6">Our Procurement Process</h2>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
              From initial inquiry to final delivery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <Card key={index} className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mb-6 border border-[#C9A55A]/20">
                    <span className="text-[#C9A55A] text-2xl font-bold">{item.step}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Syndicate */}
      <section className="py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-8">
                Why Choose Syndicate for Automotive Procurement?
              </h2>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p className="text-lg">
                  <span className="text-[#C9A55A] font-semibold">Licensed, legal, and fully compliant</span> — we operate through authorized channels with complete documentation and transparency.
                </p>
                <p className="text-lg">
                  Our North American and European dealer networks provide access to vehicles that are often unavailable through traditional channels in the Middle East.
                </p>
                <p className="text-lg">
                  Whether you're sourcing a single exotic vehicle or managing a government fleet acquisition, we handle every detail with discretion and professionalism.
                </p>
              </div>
            </div>
            <div>
              <Card className="bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10">
                <CardContent className="p-10">
                  <h3 className="text-2xl font-semibold text-white mb-6">Key Advantages</h3>
                  <div className="space-y-4">
                    {[
                      "Access to North American dealer networks",
                      "European exotic vehicle connections",
                      "Licensed import/export brokerage",
                      "Complete title and documentation services",
                      "Pre-purchase inspection coordination",
                      "Secure enclosed international shipping",
                      "Full insurance coverage during transit",
                      "Customs clearance expertise",
                      "White-glove delivery services",
                      "Fleet and government procurement experience"
                    ].map((advantage, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-[#C9A55A]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-[#C9A55A] text-xs font-bold">✓</span>
                        </div>
                        <span className="text-gray-300 leading-relaxed">{advantage}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-6">
            Ready to Source Your Next Vehicle?
          </h2>
          <p className="text-xl text-gray-400 mb-8 leading-relaxed">
            Contact us for a confidential consultation on your automotive procurement needs
          </p>
          <Button 
            asChild
            className="luxury-button bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-lg px-12 py-7"
          >
            <Link to="/contact">Request Consultation</Link>
          </Button>
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default AutomotiveProcurement;